import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  alpha,
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import { doRequest } from "../../../http";
import CardIcon from "../../Card/CardIcon";
import CardHeader from "../../Card/CardHeader";
import EnhancedTableHead from "./EnhancedTableHead";
import useDeepCompareEffect from "use-deep-compare-effect";
import PaginationV2 from "./PaginationV2";

export function useReloadState() {
  const [reloadState, setReloadState] = useState(new Date().toTimeString());

  const updateReloadState = () => {
    setReloadState(new Date().toTimeString());
  };
  return { reloadState, updateReloadState };
}

function JamezzTableV2(props) {
  const rowsPerPageOptions = useMemo(() => props.rowsPerPageOptions, [props.rowsPerPageOptions]);
  const initialPage = useMemo(() => {
    let page = 0;
    let rowsPerPage = props.defaultRowsPerPage ?? 10;
    let order = null;
    let orderBy = null;

    if (props.uuid) {
      let stateFromStorage = sessionStorage.getItem("JamezzTableV2States");
      if (stateFromStorage) {
        stateFromStorage = JSON.parse(stateFromStorage);
      } else {
        stateFromStorage = {};
      }
      if (stateFromStorage[props.uuid]) {
        page = stateFromStorage[props.uuid].page ?? 0;
        rowsPerPage = Number(stateFromStorage[props.uuid].rowsPerPage ?? props.defaultRowsPerPage ?? 10);
        if (!rowsPerPageOptions.includes(rowsPerPage)) {
          rowsPerPage = props.defaultRowsPerPage ?? 10;
        }
        order = stateFromStorage[props.uuid].order ?? null;
        orderBy = stateFromStorage[props.uuid].orderBy ?? null;
      }
    }

    return [Number(page), rowsPerPage, order, orderBy];
  }, []);

  const [order, setOrder] = useState(initialPage[2]);
  const [orderBy, setOrderBy] = useState(initialPage[3]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(initialPage[0]);
  const [rowsPerPage, setRowsPerPage] = useState(initialPage[1]);
  const [paginationData, setPaginationData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAutoRenewing, setIsAutoRenewing] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const cursor = useRef(null);

  const request = useRef(null);

  const tableOptions = useMemo(() => {
    return {
      rowsPerPage,
      selected,
      showArchive,
    };
  }, [rowsPerPage, selected, showArchive]);

  useDeepCompareEffect(() => {
    reloadDataFromServer();
  }, [page, rowsPerPage, order, orderBy, props.filters, props.reloadState, showArchive]);

  useEffect(() => {
    let interval = null;
    if (isAutoRenewing) {
      interval = setInterval(() => {
        reloadDataFromServer();
      }, 30000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAutoRenewing]);

  const reloadDataFromServer = () => {
    setLoading(true);
    let url = `${props.endPoint}/search?per_page=${rowsPerPage}&page=${page + 1}`;
    if (order && orderBy) {
      url = url + `&sort_field=${orderBy}&sort_dir=${order}`;
    }

    if (showArchive) {
      url += "&deleted=true";
    }

    if (cursor.current) {
      const cursorUrl = new URL(cursor.current);
      url += "&cursor=" + cursorUrl.searchParams.get("cursor");
    }

    const formData = {};
    if (props.filters) {
      Object.keys(props.filters).forEach((key) => {
        formData[key] = props.filters[key];
      });
    }
    if (request.current) {
      request.current.cancel();
    }

    doRequest(
      "post",
      url,
      formData,
      (response) => {
        if (response.data.data.data.length === 0) {
          setPage(0);
        }

        if (props.onGetData) {
          props.onGetData(response.data.data.data);
        }
        setLoading(false);

        if (props.sort) {
          setData(response.data.data.data.sort(props.sort));
        } else {
          setData(response.data.data.data);
        }

        delete response.data.data.data;
        setPaginationData(response.data.data);

        setSelected([]);
        request.current = null;
      },
      (err) => {
        console.log(err);
      },
      { showSpinner: false, showToastSuccess: false },
      null,
      (cancelToken) => {
        request.current = cancelToken;
      }
    );
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.filter((n) => props.rowIsSelectable(n)).map((n) => n[props.primaryKey]);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      cursor.current = paginationData?.next_page_url;
    } else {
      cursor.current = paginationData?.prev_page_url;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page === 0 ? 0 : rowsPerPage - (data?.length ?? rowsPerPage);

  const headerCells = useMemo(() => {
    return props.renderHeaderCells();
  }, [props.renderHeaderCells]);

  const selectables = useMemo(() => {
    const selectables = {};
    data?.forEach((row) => {
      if (props.rowIsSelectable(row)) {
        selectables[row[props.primaryKey]] = true;
      }
    });
    return selectables;
  }, [props.primaryKey, props.rowIsSelectable, data]);

  useEffect(() => {
    if (props.uuid) {
      let stateFromStorage = sessionStorage.getItem("JamezzTableV2States");
      if (stateFromStorage) {
        stateFromStorage = JSON.parse(stateFromStorage);
      } else {
        stateFromStorage = {};
      }
      if (!stateFromStorage[props.uuid]) {
        stateFromStorage[props.uuid] = {};
      }
      stateFromStorage[props.uuid].page = page;
      stateFromStorage[props.uuid].rowsPerPage = rowsPerPage;
      stateFromStorage[props.uuid].order = order;
      stateFromStorage[props.uuid].orderBy = orderBy;

      sessionStorage.setItem("JamezzTableV2States", JSON.stringify(stateFromStorage));
    }
  }, [page, rowsPerPage, order, orderBy]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper style={{ marginTop: 24, display: "flex", flexDirection: "column", position: "relative" }}>
        <CardHeader color={"primary"} icon>
          <CardIcon color={"primary"}>{props.headerIcon}</CardIcon>
        </CardHeader>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            ...(selected.length > 0 && {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
            }),
          }}
        >
          {selected.length > 0 ? (
            <Typography color={"inherit"} component={"div"} sx={{ flex: "1 1 100%" }} variant={"subtitle1"}>
              {selected.length} selected
            </Typography>
          ) : (
            <Typography component={"div"} id={"tableTitle"} sx={{ flex: "1 1 100%" }} variant={"h6"}>
              {props.tableTitle}
            </Typography>
          )}

          {selected.length > 0 ? props.renderSelectionActions(selected, tableOptions) : null}
        </Toolbar>
        {!paginationData || loading ? (
          <Paper
            style={{
              position: "absolute",
              backgroundColor: "#0004",
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color={"primary"} style={{ margin: "8px auto" }} />
          </Paper>
        ) : null}
        {
          <>
            {rowsPerPage > 100 ? (
              <PaginationV2
                deleteKey={props.delete}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                isAutoRenewing={isAutoRenewing}
                page={page}
                paginationData={paginationData}
                reloadDataFromServer={reloadDataFromServer}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                setIsAutoRenewing={setIsAutoRenewing}
                setShowArchive={setShowArchive}
                showArchive={showArchive}
                showAutoRenew={props.showAutoRenew}
                showRefresh={props.showRefresh}
              />
            ) : null}

            {props.autocompleteColumns ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>{props.autocompleteColumns}</Box>
            ) : null}

            {paginationData ? (
              <TableContainer>
                <Table aria-labelledby={"tableTitle"} size={props.dense ? "small" : "medium"} sx={{ minWidth: 750 }}>
                  <EnhancedTableHead
                    checkboxSelection={props.checkboxSelection}
                    headerCells={headerCells}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    order={order}
                    orderBy={orderBy}
                    rowCount={Object.keys(selectables).length}
                  />
                  <TableBody>
                    {data?.map((row, index) => {
                      const isItemSelected = isSelected(row[props.primaryKey]);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          aria-checked={isItemSelected}
                          key={row[props.primaryKey]}
                          selected={isItemSelected}
                          tabIndex={-1}
                          hover
                          // onClick={(event) => handleClick(event, row[props.primaryKey])}
                          role="checkbox"
                        >
                          {props.checkboxSelection && typeof props.checkboxSelection !== "function" ? (
                            <TableCell padding={"checkbox"} style={{ padding: "0 0 0 16px" }}>
                              <Checkbox
                                checked={isItemSelected}
                                color={"primary"}
                                disabled={!props.rowIsSelectable(row)}
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                                onClick={(event) => handleCheckboxClick(event, row[props.primaryKey])}
                              />
                            </TableCell>
                          ) : null}
                          {props.checkboxSelection && typeof props.checkboxSelection === "function"
                            ? props.checkboxSelection(
                                <TableCell padding={"checkbox"} style={{ padding: "0 0 0 16px" }}>
                                  <Checkbox
                                    checked={isItemSelected}
                                    color={"primary"}
                                    disabled={!props.rowIsSelectable(row)}
                                    inputProps={{
                                      "aria-labelledby": labelId,
                                    }}
                                    onClick={(event) => handleCheckboxClick(event, row[props.primaryKey])}
                                  />
                                </TableCell>
                              )
                            : null}
                          {props.renderCellsForRow(row, props.rowIsEditable(row), tableOptions)}
                          {props.renderRowActions(row, tableOptions)}
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 ? (
                      <TableRow
                        style={{
                          height: (props.dense ? 33 : 53) * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            <PaginationV2
              deleteKey={props.delete}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              isAutoRenewing={isAutoRenewing}
              page={page}
              paginationData={paginationData}
              reloadDataFromServer={reloadDataFromServer}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
              setIsAutoRenewing={setIsAutoRenewing}
              setShowArchive={setShowArchive}
              showArchive={showArchive}
              showAutoRenew={props.showAutoRenew}
              showRefresh={props.showRefresh}
            />
          </>
        }
      </Paper>
    </Box>
  );
}

JamezzTableV2.propTypes = {
  checkboxSelection: PropTypes.bool,
  renderCellsForRow: PropTypes.func.isRequired,
  renderHeaderCells: PropTypes.func.isRequired,
  renderRowActions: PropTypes.func.isRequired,
  renderSelectionActions: PropTypes.func.isRequired,
  rowIsEditable: PropTypes.func.isRequired,
  rowIsSelectable: PropTypes.func.isRequired,
  tableTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  primaryKey: PropTypes.string,
  rowsPerPageOptions: PropTypes.array,
  uuid: PropTypes.string, // Unique identifier used for saving state in session storage

  showRefresh: PropTypes.bool,
  showAutoRenew: PropTypes.bool,
  delete: PropTypes.string,
  defaultRowsPerPage: PropTypes.number,
  dense: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  sort: PropTypes.any,
  headerIcon: PropTypes.any,
  onGetData: PropTypes.func,
  endPoint: PropTypes.string.isRequired,
  reloadState: PropTypes.any,
};

JamezzTableV2.defaultProps = {
  checkboxSelection: false,
  primaryKey: "id",
  rowsPerPageOptions: [10, 25, 50, 100, 250, 1000],
  rowIsSelectable: () => true,
  rowIsEditable: () => true,
  uuid: "",
  showRefresh: false,
  showAutoRenew: false,
};

export default JamezzTableV2;
